<template>
  <div
    :class="`mza mza--${size} mza--${direction}`"
    :aria-label="`kompatibel mit ${compatibleProduct}`"
    @click.prevent.stop="toggleInnerText"
  >
    <span
      class="hidden"
      aria-hidden="true"
    >
      kompatibel mit {{ compatibleProduct }}
    </span>
    <div
      aria-hidden="true"
      class="mza__collapsible"
    >
      <div
        class="mza__collapsible-inner"
        :class="showInnerTextClass"
      >
        <div class="mza__collapsible__text">
          <span>
            works with <br /><span class="mza__collapsible__text--highlight">{{
              compatibleProduct
            }}</span>
          </span>
        </div>
      </div>
    </div>
    <SvgIcon
      aria-hidden
      class="icon mza__icon"
      :image="version === 'mza' ? mzaIcon : mshIcon"
    />
  </div>
</template>

<script setup lang="ts">
import mzaIcon from '~/assets/images/mza-logo-magenta-solid.svg';
import mshIcon from '~/assets/images/msh-logo.svg';

const props = defineProps({
  size: {
    type: String,
    default: 'medium',
    validator(value) {
      return ['small', 'medium', 'large'].includes(value as string);
    },
  },
  direction: {
    type: String,
    default: 'left',
    validator(value) {
      return ['left', 'right'].includes(value as string);
    },
  },
  version: {
    type: String,
    default: 'mza',
    validator(value) {
      return ['mza', 'msh'].includes(value as string);
    },
  },
});

const compatibleProduct = computed(() =>
  props.version === 'mza' ? 'MagentaZuhause App' : 'Magenta SmartHome App'
);

const isHovered = ref<boolean>(false);

const showInnerTextClass = computed(() => ({
  'mza__collapsible-inner-open': isHovered.value,
  'mza__collapsible-inner-close': !isHovered.value,
}));

const toggleInnerText = () => {
  isHovered.value = !isHovered.value;
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.mza {
  $size-sm: 2 * $grid-base;
  $size-md: 3 * $grid-base;
  $size-lg: 4 * $grid-base;

  height: $size-md;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;

  &__collapsible {
    overflow: hidden;
    &-inner {
      flex-direction: row;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 4px;
      background-color: var(--color-primary);
      padding: 0 15px 0 $grid-base;
      height: calc($size-md - 2px);
      transition: all 0.3s ease;
    }
    &__text {
      width: max-content;
      text-align: left;
      color: var(--color-white-fixed);
      @include font-size(12px, 12px);
      display: flex;
      align-items: center;
      &--highlight {
        font-weight: 600;
      }
    }
  }
  &__icon {
    width: $size-md;
    height: $size-md;
    z-index: 1;
  }

  &--small {
    height: $size-sm;

    .mza {
      &__icon {
        width: $size-sm;
        height: $size-sm;
      }

      &__collapsible {
        &-inner {
          height: calc($size-sm - 2px);
        }

        &__text {
          @include font-size(8px, 8px);
        }
      }
    }
  }

  &--large {
    height: $size-lg;

    .mza {
      &__icon {
        width: $size-lg;
        height: $size-lg;
      }

      &__collapsible {
        &-inner {
          height: calc($size-lg - 2px);
        }

        &__text {
          @include font-size(12px, 12px);
        }
      }
    }
  }

  &--left {
    @media (hover: hover) {
      &:hover {
        .mza__collapsible-inner {
          margin-right: -5px;
        }
      }
    }
    .mza__collapsible {
      margin-right: -5px;

      &-inner {
        margin-right: -5px;

        @include mq($mq-sm) {
          margin-right: -200px;
        }
      }

      &-inner-open {
        @include mq(false, $mq-md - 1) {
          margin-right: -5px;
        }
      }
      &-inner-close {
        @include mq(false, $mq-md - 1) {
          margin-right: -200px;
        }
      }
    }
  }

  &--right {
    flex-direction: row-reverse;
    position: relative;

    @media (hover: hover) {
      &:hover {
        .mza__collapsible-inner {
          margin-left: -5px;
        }
      }
    }
    .mza__collapsible {
      margin-left: -5px;
      position: absolute;
      left: $size-md;
      &-inner {
        flex-direction: row-reverse;
        margin-left: -5px;

        @include mq($mq-sm) {
          margin-left: -200px;
        }
      }

      &-inner-open {
        @include mq(false, $mq-md - 1) {
          margin-left: -5px;
        }
      }
      &-inner-close {
        @include mq(false, $mq-md - 1) {
          margin-left: -200px;
        }
      }
    }

    &.mza--small {
      .mza__collapsible {
        left: $size-sm;
      }
    }

    &.mza--large {
      .mza__collapsible {
        left: $size-lg;
      }
    }
  }
}
</style>
